import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { authAPI } from '../services/api';
import { API_BASE } from '../services/apiBase';
import { Settings as SettingsIcon, Shield, Key, User } from 'lucide-react';
import toast from 'react-hot-toast';

// The interactive API docs live at the API's own origin, not under /api/v1.
const API_DOCS = `${API_BASE.replace(/\/api\/v1\/?$/, '')}/docs`;

export default function Settings() {
  const { user } = useAuth();
  const [mfaSetup, setMfaSetup] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [loading, setLoading] = useState(false);

  const setupMfa = async () => {
    setLoading(true);
    try {
      const res = await authAPI.setupMfa ? authAPI.setupMfa() : fetch('/api/v1/auth/mfa/setup', { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }).then(r => r.json());
      setMfaSetup(res.data || res);
    } catch { toast.error('Failed to initiate MFA setup'); }
    finally { setLoading(false); }
  };

  const verifyMfa = async () => {
    try {
      await fetch('/api/v1/auth/mfa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        body: JSON.stringify({ code: verifyCode })
      });
      toast.success('MFA enabled successfully');
      setMfaSetup(null);
    } catch { toast.error('Invalid code'); }
  };

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Settings</h1><p className="page-subtitle">Account and security settings</p></div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 600 }}>
        {/* Account info */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <User size={18} color="var(--accent)" />
            <h3 style={{ fontSize: 15, fontWeight: 700 }}>Account</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Full Name</span>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{user?.full_name}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Email</span>
              <span style={{ fontSize: 14 }}>{user?.email}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Role</span>
              <span className="badge badge-purple">{user?.roles?.[0]?.name}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>MFA</span>
              <span className={`badge badge-${user?.mfa_enabled ? 'green' : 'red'}`}>{user?.mfa_enabled ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>
        </div>

        {/* MFA setup */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <Shield size={18} color="var(--accent)" />
            <h3 style={{ fontSize: 15, fontWeight: 700 }}>Two-Factor Authentication</h3>
          </div>
          {!mfaSetup ? (
            <div>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Protect your account with an authenticator app (Google Authenticator, Authy, etc.)
              </p>
              <button className="btn btn-primary" onClick={setupMfa} disabled={loading || user?.mfa_enabled}>
                <Shield size={14} /> {user?.mfa_enabled ? 'MFA Already Enabled' : 'Set Up MFA'}
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Scan this QR code with your authenticator app:</p>
              {mfaSetup.qr_code_base64 && (
                <img src={`data:image/png;base64,${mfaSetup.qr_code_base64}`} alt="MFA QR Code" style={{ width: 200, height: 200, borderRadius: 10, border: '1px solid var(--border)' }} />
              )}
              {mfaSetup.secret && (
                <div style={{ background: 'var(--bg-elevated)', padding: '10px 14px', borderRadius: 6, fontFamily: 'DM Mono, monospace', fontSize: 14, letterSpacing: '0.1em', color: 'var(--accent-bright)' }}>
                  {mfaSetup.secret}
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Verify Code</label>
                <input type="text" className="form-input mono" value={verifyCode} onChange={e => setVerifyCode(e.target.value)} placeholder="000000" maxLength={6} style={{ letterSpacing: '0.3em', fontSize: 20, textAlign: 'center' }} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-ghost" onClick={() => setMfaSetup(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={verifyMfa}>Verify & Enable MFA</button>
              </div>
            </div>
          )}
        </div>

        {/* API info */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <Key size={18} color="var(--accent)" />
            <h3 style={{ fontSize: 15, fontWeight: 700 }}>API Access</h3>
          </div>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 12 }}>API base URL for integrations:</p>
          <div style={{ background: 'var(--bg-elevated)', padding: '10px 14px', borderRadius: 6, fontFamily: 'DM Mono, monospace', fontSize: 13, color: 'var(--green)' }}>
            {API_BASE}
          </div>
          <div style={{ marginTop: 12 }}>
            <a href={API_DOCS} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
              View API Docs →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
